import React from 'react';

import { useAppStaticContext } from './AppContext';
import { appConfig } from './config';
import { useNavigate } from './router/useNavigate';
import { violetta } from './violettaSdk';

// L'utente ha provato un'azione della chat che richiede un account ed è guest:
// violetta lascia decidere all'host cosa mostrare (vedi il commento sull'evento
// "requestLogin" in violettaSdk.ts). Qui si traduce l'intent nell'unico
// meccanismo che lo store già usa per lo stesso scopo altrove (Cta.tsx,
// ReferralBox.tsx): `onLoginActionCallback` per l'invito al login, e la pagina
// fissa di registrazione per l'invito a registrarsi.
//
// Registrazione una sola volta per caricamento di pagina, e non per istanza del
// componente: l'sdk accumula gli handler e non espone un "off", quindi ogni
// registrazione in più è un'apertura in più per ogni richiesta. Un `useEffect`
// con dipendenze vuote NON basta: gli store girano in `React.StrictMode`, che in
// sviluppo esegue setup, cleanup e di nuovo setup, e qui un cleanup che disfi la
// registrazione non può esistere. Stessa forma e stessa ragione di
// useViolettaAddToCart.
let registered = false;
// Per la stessa ragione l'handler registrato non può catturare `getFixedPagePath`/
// `navigate`/`onLoginActionCallback` dalla closure del primo mount: la
// registrazione è una sola e per sempre, quindi il riferimento alla versione
// corrente sta nel modulo e non in una ref.
let currentHandler: ((intent?: 'login' | 'register') => void) | undefined;

export const useViolettaRequestLogin = (enable: boolean) => {
  const { getFixedPagePath, onLoginActionCallback } = useAppStaticContext();
  const navigate = useNavigate();

  const handleRequestLogin = React.useCallback(
    (intent?: 'login' | 'register') => {
      // Chiude il widget PRIMA di aprire l'overlay dello store: se lo store lo
      // apre come reazione sincrona (offcanvas) o come navigazione (register), in
      // entrambi i casi il pannello del widget, se ancora aperto, resterebbe
      // sopra per via del proprio z-index. Solo gli store che hanno acceso il
      // flag lo pagano: il comando non fa nulla se il widget non è montato.
      if (appConfig.violetta?.closeWidgetOnRequestLogin) {
        violetta.command('closeWidget');
      }

      if (intent === 'register') {
        navigate(getFixedPagePath('register'));
      } else {
        onLoginActionCallback?.();
      }
    },
    [getFixedPagePath, navigate, onLoginActionCallback],
  );

  React.useEffect(() => {
    currentHandler = handleRequestLogin;
  }, [handleRequestLogin]);

  React.useEffect(() => {
    if (!enable || registered) {
      return;
    }

    registered = true;
    violetta.command('on', 'requestLogin', ({ intent } = {}) => {
      currentHandler?.(intent);
    });
  }, [enable]);
};
